/**
 * Client-side autocomplete against the Base Adresse Nationale.
 *
 * The BAN (served via Géoplateforme at data.geopf.fr/geocodage) is:
 *  - free, no API key, CORS-enabled
 *  - rate-limited per IP (generous — plenty for human typing)
 *  - the same backend the `geocodeAddress` Cloud Function queries server-side
 *
 * Endpoint note: the legacy host api-adresse.data.gouv.fr still responds but
 * sends an `x-api-notice` header announcing its decommissioning in favour of
 * `data.geopf.fr/geocodage/`. We call the new host directly so we don't have
 * to repatch when the legacy host goes away.
 *
 * Wiring autocomplete on the client buys us two things:
 *   1. immediate feedback as the user types — they can pick the exact address
 *      before we spend any pipeline quota;
 *   2. when the user picks a suggestion we already have lat/lng/insee, so
 *      `runPipeline` can skip the first Cloud Function call entirely
 *      (see `prefetchedGeocode` in pipeline.ts).
 */

import { useEffect, useState } from "react";
import type { GeocodeResult } from "./types";

const BAN_ENDPOINT = "https://data.geopf.fr/geocodage/search/";
const DEBOUNCE_MS = 200;

export type BanSuggestion = {
  label: string;
  score: number;
  lat: number;
  lng: number;
  city: string;
  postcode: string;
  insee: string;
  /** BAN "type": housenumber | street | locality | municipality */
  type: string;
  housenumber: string | null;
  street: string | null;
};

type BanFeature = {
  geometry: { coordinates: [number, number] };
  properties: {
    label: string;
    score: number;
    city?: string;
    postcode?: string;
    citycode?: string;
    type?: string;
    housenumber?: string;
    street?: string;
  };
};

type BanResponse = { features?: BanFeature[] };

export async function fetchBanSuggestions(
  query: string,
  opts?: { signal?: AbortSignal; limit?: number },
): Promise<BanSuggestion[]> {
  const limit = opts?.limit ?? 5;
  const url = `${BAN_ENDPOINT}?q=${encodeURIComponent(
    query,
  )}&limit=${limit}&autocomplete=1`;
  const res = await fetch(url, { signal: opts?.signal });
  if (!res.ok) throw new Error(`BAN ${res.status}`);
  const body = (await res.json()) as BanResponse;
  return (body.features ?? []).map(toSuggestion);
}

function toSuggestion(f: BanFeature): BanSuggestion {
  const [lng, lat] = f.geometry.coordinates;
  const p = f.properties;
  return {
    label: p.label,
    score: p.score,
    lat,
    lng,
    city: p.city ?? "",
    postcode: p.postcode ?? "",
    insee: p.citycode ?? "",
    type: p.type ?? "",
    housenumber: p.housenumber ?? null,
    street: p.street ?? null,
  };
}

/**
 * Convert a picked suggestion into the GeocodeResult shape our pipeline
 * stages already consume — so when the user has picked a suggestion we can
 * skip the first Cloud Function call entirely.
 */
export function suggestionToGeocodeResult(s: BanSuggestion): GeocodeResult {
  return {
    label: s.label,
    lat: s.lat,
    lng: s.lng,
    postcode: s.postcode,
    city: s.city,
    insee: s.insee,
    score: s.score,
    housenumber: s.housenumber,
    street: s.street,
  };
}

type State =
  | { status: "idle"; suggestions: BanSuggestion[] }
  | { status: "loading"; suggestions: BanSuggestion[] }
  | { status: "ready"; suggestions: BanSuggestion[] }
  | { status: "error"; suggestions: BanSuggestion[]; message: string };

/**
 * Debounced autocomplete hook. Returns the latest suggestions for the given
 * query — empty when the query is too short to be worth a round-trip.
 *
 * We keep previous suggestions visible during a new "loading" tick so the
 * dropdown doesn't flicker empty between keystrokes.
 */
export function useBanSuggestions(query: string): State {
  const [state, setState] = useState<State>({
    status: "idle",
    suggestions: [],
  });

  useEffect(() => {
    const q = query.trim();
    // Below 3 chars BAN returns noisy city-level matches; not worth the call.
    if (q.length < 3) {
      setState({ status: "idle", suggestions: [] });
      return;
    }
    const ctl = new AbortController();
    const timer = setTimeout(() => {
      setState((prev) => ({
        status: "loading",
        suggestions: prev.suggestions,
      }));
      fetchBanSuggestions(q, { signal: ctl.signal })
        .then((s) => {
          if (ctl.signal.aborted) return;
          setState({ status: "ready", suggestions: s });
        })
        .catch((e: unknown) => {
          if (ctl.signal.aborted) return;
          const msg = e instanceof Error ? e.message : String(e);
          setState({ status: "error", suggestions: [], message: msg });
        });
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      ctl.abort();
    };
  }, [query]);

  return state;
}
